import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import { useEditTemplate, usePaymentFlow } from 'app/pages/anipay/api/usePaymentFlow';
import { AniPaySchema } from 'app/pages/anipay/model/schema';
import {
  AniPayGetPaymentResponse,
  CreateAniPayTemplate,
  FormFieldValues,
} from 'app/pages/anipay/model/types';
import AccordionWrap from 'app/pages/anipay/new-payment/ui/accordion-wrap/accordion-wrap';
import AssignmentDetails from 'app/pages/anipay/new-payment/ui/assignment-details/assignment-details';
import GeneralDetails from 'app/pages/anipay/new-payment/ui/general-details/general-details';
import PaymentDetails from 'app/pages/anipay/new-payment/ui/payment-details/payment-details';
import { useCustomNavigate } from 'app/utils/customHooks/useCustomNavigate';

import BackIcon from 'app/components/Icons/BackIcon';
import SaveIcon from 'app/components/Icons/SaveIcon';
import DeleteTemplateModal from 'app/pages/TemplatesPage/TemplateModals/DeleteTemplateModal';
import { useDeleteTemplate } from 'app/utils/services/templateService';
import { formatNumber } from '../alias/add-alias-modal/add-alias-modal';
import anipayService from '../api/anipayService';
import { aniPayTypes } from '../constants';
import TemplatesSuccessDialog from '../ui/templates-success-dialog/success-dialog';

// Common API call wrapper
const useApiCall = <T, D>(mutation: any, onSuccess?: (data: T) => void) => {
  return (data: D) => {
    mutation.mutate(data, {
      onSuccess: (response: T) => {
        onSuccess?.(response);
      },
    });
  };
};

const AniPayEditTemplate = () => {
  const { t } = useTranslation();
  const { templateId } = useParams();
  const navigate = useCustomNavigate();

  const paymentFlow = usePaymentFlow();
  const saveTemplate = useEditTemplate();

  const { data: templateData } = useQuery(['ani-pay_template', templateId], () =>
    anipayService.getAniPayTemplate(templateId)
  );

  const { deleteTemplateMutation, dataDeleteTemplate } = useDeleteTemplate(true);

  const [openTemplatesSuccessModal, setOpenTemplatesSuccessModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const methods = useForm<FormFieldValues>({
    resolver: yupResolver(AniPaySchema as any),
    mode: 'onTouched',
  });

  const {
    handleSubmit,
    getValues,
    reset,
    trigger,
    setValue,
    formState: { isValid: isValidForm, isDirty },
  } = methods;

  // API call handlers
  const handlePaymentFlow = useApiCall<AniPayGetPaymentResponse, any>(paymentFlow, resp => {
    reset({
      ...resp.ips_data,
      aliasType: {
        value: templateData?.template?.data?.content_data?.aliasType,
        label: aniPayTypes?.find(
          item => item?.value === templateData?.template?.data?.content_data?.aliasType
        )?.label,
      },
      aliasValue:
        templateData?.template?.data?.content_data?.aliasType === 'MOBILE'
          ? templateData?.template?.data?.content_data?.aliasValue?.substring(3)
          : templateData?.template?.data?.content_data?.aliasValue,
      amount: templateData?.template?.data?.amount,
      contract_no: templateData?.template?.data?.content_data?.contract_no,
      description: templateData?.template?.data?.content_data?.description,
      files: templateData?.template?.data?.content_data?.files?.map((file: any) => ({
        ...file,
        name: file?.name,
        file_id: file?.file_id,
      })),
    });

    setValue(
      'account',
      resp.accounts?.find(
        account => account?.value === templateData?.template?.data?.content_data?.db_acc_no
      ),
      {
        shouldValidate: true,
      }
    );
    setValue(
      'comm_account',
      resp.accounts?.find(
        account => account?.value === templateData?.template?.data?.content_data?.com_acc_no
      ),
      {
        shouldValidate: true,
      }
    );
  });

  const handleSaveTemplate = useApiCall<any, CreateAniPayTemplate>(saveTemplate, () => {
    setOpenTemplatesSuccessModal(true);
  });

  // Handle template data
  useEffect(() => {
    if (templateData) {
      handlePaymentFlow({
        aliasType: templateData?.template?.data?.content_data?.aliasType,
        aliasValue: templateData?.template?.data?.content_data?.aliasValue,
      });
    }
  }, [templateData]);

  const handleSearch = async () => {
    const isValid = await trigger('aliasValue');
    if (!isValid) return;

    const aliasType = getValues('aliasType');
    const aliasValue =
      aliasType?.value === 'MOBILE'
        ? `994${formatNumber(getValues('aliasValue'))}`
        : getValues('aliasValue');

    handlePaymentFlow({
      aliasType: aliasType.value.toUpperCase(),
      aliasValue,
    });
  };

  const onSubmit = () => {
    const aliasValue =
      getValues('aliasType')?.value === 'MOBILE'
        ? `994${formatNumber(getValues('aliasValue'))}`
        : getValues('aliasValue');
    handleSaveTemplate({
      amount: getValues('amount'),
      title: templateData?.template?.title,
      transfer_type: 'anipay',
      id: templateData?.template?.id,
      content_data: {
        db_acc_no: getValues('account').value,
        com_acc_no: getValues('comm_account').value,
        description: getValues('description'),
        contract_no: getValues('contract_no'),
        files: getValues('files'),
        aliasType: getValues('aliasType')?.value.toUpperCase(),
        aliasValue,
      },
    });
  };

  return (
    <>
      <FormProvider {...methods}>
        <form
          style={{
            display: 'flex',
            gap: '16px',
            flexDirection: 'column',
            marginTop: '24px',
          }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <AccordionWrap title={t('transfer.generalDetails')}>
            <GeneralDetails isLoading={paymentFlow.isLoading} handleSearch={handleSearch} />
          </AccordionWrap>
          <AccordionWrap title={t('salary.paymentDetails')}>
            <PaymentDetails accounts={paymentFlow?.data?.accounts} />
          </AccordionWrap>
          <AccordionWrap title={t('transfer.destinationAndAdditions')}>
            <AssignmentDetails />
          </AccordionWrap>
          <Box display='flex' justifyContent='space-between'>
            <Button color={'secondary'} onClick={() => navigate('templates-page')}>
              <BackIcon />
              {t('commonActions.back')}
            </Button>
            <Box>
              <Button
                sx={{ mr: 2 }}
                color={'success'}
                type='submit'
                disabled={!isValidForm || !isDirty}
              >
                <SaveIcon />

                {t('transfer.saveChanges')}
              </Button>
              <Button
                color='error'
                sx={{ alignSelf: 'end' }}
                onClick={() => setOpenDeleteModal(true)}
              >
                {t('commonActions.delete')}
              </Button>
            </Box>
          </Box>
        </form>
      </FormProvider>

      <TemplatesSuccessDialog
        label={t('templates.templateSaved')}
        open={openTemplatesSuccessModal}
        handleClose={() => {
          setOpenTemplatesSuccessModal(false);
          navigate('ani-pay/alias/tin');
        }}
      />
      {openDeleteModal && (
        <DeleteTemplateModal
          open={openDeleteModal}
          close={setOpenDeleteModal}
          id={templateId}
          deleteTemplate={deleteTemplateMutation}
          isEdit={true}
          data={dataDeleteTemplate}
        />
      )}
    </>
  );
};

export default AniPayEditTemplate;
